/**
 * 客户管理API服务
 * 对接后端真实API,实现数据持久化
 */

import api from '../../api';
import type { Result } from '../../types';
import { Company } from '../../../types';
import { unwrapApiResponse } from '../../../utils/apiUtils';
import { normalizeCompanyVo } from '../../../utils/crmDtoNormalize';
import { getContacts } from './contact';

/**
 * 将前端客户对象转为后端 CompanySaveDTO 可 JSON 反序列化的请求体。
 * - industry / tags：表单多为 string[]，DTO 为 JSON 字符串
 * - website 为空串时去掉字段，避免触发后端 @Pattern（空串不匹配 URL 正则）
 */
export function toApiCompanyBody(company: Partial<Company>): Record<string, unknown> {
  const payload = { ...company } as Record<string, unknown>;

  if (company.invoiceInfo) {
    payload.invoiceTaxNo = company.invoiceInfo.taxNo;
    payload.invoiceAddress = company.invoiceInfo.companyAddress;
    payload.invoicePhone = company.invoiceInfo.companyPhone;
    payload.invoiceBankName = company.invoiceInfo.bankName;
    payload.invoiceBankAccount = company.invoiceInfo.bankAccount;
    payload.invoiceType = company.invoiceInfo.invoiceType;
    delete payload.invoiceInfo;
  }

  const ind = company.industry as unknown;
  if (ind !== undefined && ind !== null) {
    if (Array.isArray(ind)) {
      payload.industry = JSON.stringify(ind);
    }
  }

  if (company.tags !== undefined && Array.isArray(company.tags)) {
    if (company.tags.length > 0) {
      payload.tags = JSON.stringify(company.tags);
    } else {
      delete payload.tags;
    }
  }

  const web = company.website;
  if (web === '' || web === undefined) {
    delete payload.website;
  }

  return payload;
}

export interface CompanyQueryParams {
  pageNum?: number;
  pageSize?: number;
  /** 客户列表 tab：all / mine / public 等，与后端 CompanyQueryDTO 对齐 */
  tab?: string;
  keyword?: string;
  name?: string;
  status?: string;
  customerLevel?: string;
  industry?: string;
  province?: string;
  city?: string;
  ownerId?: string;
  customerSource?: string;
  scale?: string;
  tag?: string;
  createdFrom?: string;
  createdTo?: string;
  sortOrder?: string;
}

export interface PageResponse<T> {
  records: T[];
  total: number;
  size: number;
  current: number;
  pages: number;
}

export const companyService = {
  list: async (params: CompanyQueryParams = {}): Promise<PageResponse<Company>> => {
    const response = await api.get<Result<PageResponse<Record<string, unknown>>>>('/crm/company', {
      params: {
        pageNum: params.pageNum || 1,
        pageSize: params.pageSize || 10,
        ...params,
      },
    });
    const page = unwrapApiResponse<PageResponse<Record<string, unknown>>>(response);
    return {
      ...page,
      records: page.records.map((r) => normalizeCompanyVo(r)),
    };
  },

  get: async (id: string): Promise<Company> => {
    const response = await api.get<Result<Record<string, unknown>>>(`/crm/company/${id}`);
    return normalizeCompanyVo(unwrapApiResponse<Record<string, unknown>>(response));
  },

  create: async (company: Partial<Company>): Promise<Company> => {
    const response = await api.post<Result<Record<string, unknown>>>('/crm/company', toApiCompanyBody(company));
    return normalizeCompanyVo(unwrapApiResponse<Record<string, unknown>>(response));
  },

  update: async (id: string, company: Partial<Company>): Promise<Company> => {
    const response = await api.put<Result<Record<string, unknown>>>(`/crm/company/${id}`, toApiCompanyBody(company));
    return normalizeCompanyVo(unwrapApiResponse<Record<string, unknown>>(response));
  },

  delete: async (id: string): Promise<void> => {
    await api.delete<Result<void>>(`/crm/company/${id}`);
  },

  batchDelete: async (ids: string[]): Promise<void> => {
    await api.delete<Result<void>>('/crm/company/batch', { data: ids });
  },

  exportExcel: async (params: CompanyQueryParams = {}): Promise<Blob> => {
    const response = await api.get('/crm/company/export', {
      params,
      responseType: 'blob',
    });
    return response as unknown as Blob;
  },

  importExcel: async (file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    await api.post('/crm/company/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // 客户统计
  getStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/stats');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户来源统计
  getSourceStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/source-stats');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户行业统计
  getIndustryStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/industry-stats');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户地区统计
  getRegionStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/region-stats');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户级别统计
  getLevelStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/level-stats');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户规模统计
  getScaleStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/scale-stats');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户状态统计
  getStatusStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/status-stats');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户活跃度统计
  getActivityStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/activity-stats');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户价值统计
  getValueStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/value-stats');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户增长趋势
  getGrowthTrend: async (period: 'week' | 'month' | 'quarter' | 'year'): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/growth-trend', {
      params: { period },
    });
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户流失分析
  getChurnAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/churn-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户满意度统计
  getSatisfactionStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/satisfaction-stats');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户贡献度分析
  getContributionAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/contribution-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户生命周期分析
  getLifecycleAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/lifecycle-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户细分分析
  getSegmentationAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/segmentation-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户预测分析
  getForecastAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/forecast-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户风险评估
  getRiskAssessment: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/risk-assessment');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户推荐分析
  getRecommendationAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/recommendation-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户关联分析
  getAssociationAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/association-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户行为分析
  getBehaviorAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/behavior-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户偏好分析
  getPreferenceAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/preference-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户忠诚度分析
  getLoyaltyAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/loyalty-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户留存分析
  getRetentionAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/retention-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户转化分析
  getConversionAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/conversion-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户价值分层
  getValueTiering: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/value-tiering');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户健康度评估
  getHealthAssessment: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/health-assessment');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户满意度调查
  getSatisfactionSurvey: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/satisfaction-survey');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户反馈分析
  getFeedbackAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/feedback-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户投诉分析
  getComplaintAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/complaint-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户建议分析
  getSuggestionAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/suggestion-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户需求分析
  getRequirementAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/requirement-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户痛点分析
  getPainPointAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/pain-point-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户机会分析
  getOpportunityAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/opportunity-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户威胁分析
  getThreatAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/threat-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户优势分析
  getStrengthAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/strength-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户劣势分析
  getWeaknessAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/weakness-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户SWOT分析
  getSwotAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/swot-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户PEST分析
  getPestAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/pest-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户五力分析
  getFiveForcesAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/five-forces-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户价值链分析
  getValueChainAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/value-chain-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户波士顿矩阵分析
  getBcgMatrixAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/bcg-matrix-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户GE矩阵分析
  getGeMatrixAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/ge-matrix-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户安索夫矩阵分析
  getAnsoffMatrixAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/ansoff-matrix-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户SPACE矩阵分析
  getSpaceMatrixAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/space-matrix-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户QSPM矩阵分析
  getQspmMatrixAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/qspm-matrix-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户IE矩阵分析
  getIeMatrixAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/ie-matrix-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // 客户大战略矩阵分析
  getGrandStrategyMatrixAnalysis: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<Result<Record<string, unknown>>>('/crm/company/grand-strategy-matrix-analysis');
    return unwrapApiResponse<Record<string, unknown>>(response);
  },

  // ========== 公海池操作 ==========

  /** 将客户移入公海池（移出） */
  toPublicPool: async (id: string): Promise<void> => {
    const res = (await api.put<Result<void>>(`/crm/company/${id}/to-public-pool`)) as unknown as Result<void>;
    if (res?.code !== 200) {
      throw new Error(res?.message || res?.msg || '移入公海失败');
    }
  },

  /** 从公海池领取客户（选入） */
  claimFromPublicPool: async (id: string, ownerId: string, ownerName: string): Promise<void> => {
    const res = (await api.put<Result<void>>(`/crm/company/${id}/claim`, null, {
      params: { ownerId, ownerName },
    })) as unknown as Result<void>;
    if (res?.code !== 200) {
      throw new Error(res?.message || res?.msg || '从公海领取失败');
    }
  },

  /** 批量将客户移入公海池 */
  batchToPublicPool: async (ids: string[]): Promise<void> => {
    const res = (await api.put<Result<void>>('/crm/company/batch/to-public-pool', ids)) as unknown as Result<void>;
    if (res?.code !== 200) {
      throw new Error(res?.message || res?.msg || '批量移入公海失败');
    }
  },

  /** 批量从公海池领取客户 */
  batchClaimFromPublicPool: async (ids: string[], ownerId: string, ownerName: string): Promise<void> => {
    const res = (await api.put<Result<void>>('/crm/company/batch/claim', ids, {
      params: { ownerId, ownerName },
    })) as unknown as Result<void>;
    if (res?.code !== 200) {
      throw new Error(res?.msg || '批量领取失败');
    }
  },

  /** 分配负责人 */
  assignCompany: async (id: string, owner: string): Promise<void> => {
    const response = await api.post<Result<void>>(`/crm/company/${id}/assign`, { owner });
    unwrapApiResponse(response);
  },

  /** 更新负责人（批量转移用，对应 PUT /crm/company/{id}/owner） */
  updateOwner: async (id: string, ownerId: string, ownerName: string): Promise<void> => {
    const response = await api.put<Result<void>>(`/crm/company/${id}/owner`, null, {
      params: { ownerId, ownerName },
    });
    unwrapApiResponse(response);
  },

  /** 转让客户 */
  transferCompany: async (id: string, toOwner: string): Promise<void> => {
    const response = await api.post<Result<void>>(`/crm/company/${id}/transfer`, { toOwner });
    unwrapApiResponse(response);
  },

  /** 认领客户（旧 POST /claim，与公海 PUT 领取并存） */
  claimCompany: async (id: string): Promise<void> => {
    const response = await api.post<Result<void>>(`/crm/company/${id}/claim`);
    unwrapApiResponse(response);
  },

  // ========== 转让历史 ==========

  /** 获取客户转让历史 */
  getTransferHistory: async (companyId: string): Promise<Record<string, unknown>[]> => {
    const response = await api.get<Result<Record<string, unknown>[]>>(`/crm/company/${companyId}/transfer-history`);
    return unwrapApiResponse<Record<string, unknown>[]>(response);
  },
};

/**
 * 快速获取四个 Tab 的客户/联系人数量
 * 并发发送 4 个 pageSize=1 的请求，仅获取 total 字段，秒级返回
 */
export async function getTabCounts(): Promise<{ my: number; public: number; all: number; contacts: number }> {
  const [myRes, pubRes, allRes, contactRes] = await Promise.allSettled([
    companyService.list({ tab: 'my', pageNum: 1, pageSize: 1 }),
    companyService.list({ tab: 'public', pageNum: 1, pageSize: 1 }),
    companyService.list({ tab: 'all', pageNum: 1, pageSize: 1 }),
    getContacts({ pageNum: 1, pageSize: 1 }),
  ]);

  const pick = (r: PromiseSettledResult<unknown>) =>
    r.status === 'fulfilled' ? Number((r.value as { total?: number }).total ?? 0) : 0;

  // contacts 响应体结构是 { code?, data: { total?, records? } }
  const contactTotal =
    contactRes.status === 'fulfilled'
      ? Number(((contactRes.value as unknown) as { data?: { total?: number } }).data?.total ?? 0)
      : 0;

  return {
    my: pick(myRes),
    public: pick(pubRes),
    all: pick(allRes),
    contacts: contactTotal,
  };
}
